/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ExtensionFormView.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/


import { useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Phone, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { GeneralTab, VoicemailTab, RecordingTab, ContactTab, AdvancedTab, FollowMeTab, ForwardingsTab, PersonalAssistantTab } from './tabs';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { deleteExtensionById, navigateToExtensionDuplicate } from './extensionsActions';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useExtensionData } from './hooks/useExtensionData';
import { useExtensionForm } from './hooks/useExtensionForm';
import { useExtensionQuickList } from './hooks/useExtensionQuickList';
import { useExtensionOriginalData } from './hooks/useExtensionOriginalData';
import { ExtensionQuickListModal } from './components/ExtensionQuickListModal';
import type { TabType } from './types';

export default function ExtensionFormView() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const duplicateId = searchParams.get('duplicate');
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();

    const mode = id && id !== 'new' ? 'edit' : 'create';
    const isEditMode = mode === 'edit';

    const [showQuickList, setShowQuickList] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState<TabType>('general');

    // ── Data & Form hooks ─────────────────────────────────────
    const {
        formData, setFormData, voicemailData, setVoicemailData,
        recordingSettings, setRecordingSettings, contactInfo, setContactInfo,
        followMeData, setFollowMeData, personalAssistantData, setPersonalAssistantData,
        options, isLoading, isLoadingOptions,
    } = useExtensionData({ id, duplicateId, isEditMode, selectedDomainId: selectedDomain?.id });

    // ── Original data capture ─────────────────────────────────
    const {
        originalFormData, setOriginalFormData,
        originalVoicemailData, setOriginalVoicemailData,
        originalRecordingSettings, setOriginalRecordingSettings,
        originalContactInfo, setOriginalContactInfo,
        originalFollowMeData, setOriginalFollowMeData,
    } = useExtensionOriginalData({
        isLoading, isEditMode, extensionId: id, duplicateId,
        formData, voicemailData, recordingSettings, contactInfo, followMeData, personalAssistantData,
    });

    // ── Form validation & submission ──────────────────────────
    const { errors, isSaving, validateForm, handleSave: handleSaveForm, handleCancel: handleCancelForm } = useExtensionForm({
        formData, setFormData, voicemailData, setVoicemailData,
        recordingSettings, setRecordingSettings, contactInfo, setContactInfo,
        followMeData, setFollowMeData,
        originalFormData, originalVoicemailData, originalRecordingSettings, originalContactInfo, originalFollowMeData,
        isEditMode, extensionId: id,
    });

    // ── Save with tab navigation on error ─────────────────────
    const handleSaveInternal = useCallback(async () => {
        const validation = validateForm();
        if (!validation.isValid) {
            const keys = Object.keys(validation.errors);
            if (keys.some((k) => k.startsWith('vm'))) { setActiveTab('voicemail'); showError(t('extensions.messages.voicemailValidationError')); }
            else if (keys.some((k) => k.includes('device') || k.includes('Device'))) { setActiveTab('general'); showError(t('extensions.messages.deviceValidationError')); }
            else if (keys.includes('forwardTo')) { setActiveTab('advanced'); showError(t('extensions.messages.virtualValidationError')); }
            else { setActiveTab('general'); showError(t('extensions.messages.validationError')); }
            return undefined;
        }
        const savedData = await handleSaveForm();
        if (!savedData && isEditMode && id) return { id, uuid: id, ...formData };
        if (savedData) {
            setOriginalFormData({ ...formData });
            setOriginalVoicemailData({ ...voicemailData });
            setOriginalRecordingSettings({ ...recordingSettings });
            setOriginalContactInfo({ ...contactInfo });
            setOriginalFollowMeData({ ...followMeData });
        }
        return savedData;
    }, [validateForm, handleSaveForm, formData, voicemailData, recordingSettings, contactInfo, followMeData, isEditMode, id, t, showError, setOriginalFormData, setOriginalVoicemailData, setOriginalRecordingSettings, setOriginalContactInfo, setOriginalFollowMeData]);

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/pbx/extensions/extensions',
        newPath: '/pbx/extensions/extensions/new',
        mode,
        getEditPath: (saved) => `/pbx/extensions/extensions/${saved.uuid || saved.id}`,
        getFormData: () => ({ formData, voicemailData, recordingSettings, contactInfo, followMeData }),
        storageKey: 'extensions:saveAndNewData',
    });

    // ── Delete ────────────────────────────────────────────────
    const handleDeleteConfirm = async () => {
        if (!isEditMode || !id) return;
        setIsDeleting(true);
        try { await deleteExtensionById(id); showSuccess(t('extensions.messages.deleteSuccess')); navigate('/pbx/extensions/extensions'); }
        catch (err) { showError(err instanceof Error ? err.message : t('extensions.messages.deleteError')); setShowDeleteModal(false); }
        finally { setIsDeleting(false); }
    };

    // ── Quick list ────────────────────────────────────────────
    const { quickListExtensions, quickListSearch, setQuickListSearch, isSearching, isLoadingInitial } = useExtensionQuickList({ isOpen: showQuickList });
    const closeQuickList = () => { setShowQuickList(false); setQuickListSearch(''); };

    // ── Loading ───────────────────────────────────────────────
    if (isLoading) return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;

    // ── Render ────────────────────────────────────────────────
    const setField = (setter: React.Dispatch<React.SetStateAction<any>>) =>
        (field: string, value: unknown) => setter((prev: any) => ({ ...prev, [field]: value }));

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Phone className="h-5 w-5 text-primary" />}
                title={isEditMode ? `${t('extensions.editExtension')} - ${formData.extension || ''}` : t('extensions.createExtension')}
                backButtonText={t('extensions.actions.list')}
                onBack={() => navigate('/pbx/extensions/extensions')}
                actions={[
                    { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('extensions.actions.quickList'), onClick: () => setShowQuickList(true) },
                    ...(isEditMode ? [{ icon: <Copy className="h-5 w-5 text-muted-foreground" />, tooltip: t('extensions.actions.duplicate'), onClick: () => navigateToExtensionDuplicate(navigate, id!) }] : []),
                    { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('extensions.actions.add'), onClick: () => navigate('/pbx/extensions/extensions/new') },
                ]}
            />

            <div className="mt-8">
                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabType)}>
                    <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1 overflow-x-auto flex-nowrap">
                        {(['general','voicemail','recording','advanced','forwardings','followMe','personalAssistant','contact'] as const).map((tab) => (
                            <TabsTrigger key={tab} value={tab}>{t(`extensions.tabs.${tab}`, tab)}</TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="general"><GeneralTab formData={formData} errors={errors} isEditMode={isEditMode} classOfServices={options.classOfServices} dialProfiles={options.dialProfiles} agentProfiles={options.agentProfiles} sipProfiles={options.sipProfiles} smsNumbers={options.smsNumbers || []} dispatchLocations={options.dispatchLocations || []} isLoadingOptions={isLoadingOptions} onChange={setField(setFormData)} /></TabsContent>
                    <TabsContent value="voicemail"><VoicemailTab formData={formData} voicemailData={voicemailData} errors={errors} onChange={setField(setFormData)} onVoicemailChange={setField(setVoicemailData)} faxDevices={options.faxDevices} /></TabsContent>
                    <TabsContent value="recording"><RecordingTab recordingSettings={recordingSettings} onChange={setField(setRecordingSettings)} /></TabsContent>
                    <TabsContent value="advanced"><AdvancedTab formData={formData} errors={errors} onChange={setField(setFormData)} aiAgents={options.aiAgents} aiTranslatorProfiles={options.aiTranslatorProfiles} /></TabsContent>
                    <TabsContent value="forwardings"><ForwardingsTab followMeData={followMeData} onFollowMeChange={setField(setFollowMeData)} /></TabsContent>
                    <TabsContent value="followMe"><FollowMeTab followMeData={followMeData} onChange={setField(setFormData)} onFollowMeChange={setField(setFollowMeData)} /></TabsContent>
                    <TabsContent value="personalAssistant"><PersonalAssistantTab personalAssistantData={personalAssistantData} onPersonalAssistantChange={setField(setPersonalAssistantData)} /></TabsContent>
                    <TabsContent value="contact"><ContactTab contactInfo={contactInfo} onChange={setField(setContactInfo)} /></TabsContent>
                </Tabs>
            </div>

            <FixedActionBar onSaveWithAction={handleSaveWithAction} onCancel={handleCancelForm} onDelete={() => { if (isEditMode && id) setShowDeleteModal(true); }} isEditMode={isEditMode} isSaving={isSaving} cancelText={t('extensions.actions.cancel')} deleteText={t('extensions.actions.delete')} useSaveDropdown saveDropdownStorageKey="extensions:saveAction" saveDisabled={isSaving} />

            <ConfirmationModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={handleDeleteConfirm} title={t('extensions.messages.confirmDeleteTitle')} message={t('extensions.messages.confirmDeleteMessage', { name: formData.name || formData.extension })} confirmText={t('extensions.actions.delete')} cancelText={t('common.cancel')} type="danger" isLoading={isDeleting} />

            <ExtensionQuickListModal isOpen={showQuickList} currentExtensionId={id} extensions={quickListExtensions} isLoadingInitial={isLoadingInitial} isSearching={isSearching} searchValue={quickListSearch} onSearchChange={setQuickListSearch} onClose={closeQuickList} />
        </div>
    );
}
